import React, { useEffect, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { hot } from 'react-hot-loader/root';
import { useStore } from 'effector-react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { queryClient } from '@queryClient';
import { SandTableType, type STMatrix } from '@types';
import getTid from '@utils/getTid';
import { $isSDK } from '@stores/sdkStores';
import { $tourData } from '@stores/dataStores';
import { $memberApi } from '@stores/memberStores';
import PageLoading from '@components/PageLoading';
import Noti from '@components/Noti';
import { Header } from '@modules/header';
import { Toolbar } from '@modules/toolbar';
import { fetchSandTableMatrices } from '@modules/sandTable';
import { setDocumentTtitle } from '@utils/setDocumentTtitle';
import { fetchData } from '@modules/edit';
import useOwnerIdCheck from '@hooks/useOwnerIdCheck';
import PageMain from './pages/PageMain';
import PageSandTables from './pages/PageSandTables';

function LayoutPage() {
  return (
    <div className="fixed inset-0 flex flex-col bg-black px-10 pt-15 pb-10 text-base text-white">
      <Header />
      <Outlet />
    </div>
  );
}

function LayoutToolBar() {
  return (
    <div className="mt-15 flex flex-1">
      <Toolbar />
      <Outlet />
    </div>
  );
}

function App() {
  const isSDK = useStore($isSDK);
  const data = useStore($tourData);
  const [isError, setIsError] = useState(false);

  useOwnerIdCheck();

  const init = async () => {
    await $memberApi.fetchMember();
    try {
      const { sandBox = [], name } = await fetchData();
      setDocumentTtitle(name);
      const ids = sandBox
        .filter((x) => x.type === SandTableType.matrix)
        .map((x: STMatrix) => x.data);
      if (ids) fetchSandTableMatrices(ids);
    } catch (error) {
      setIsError(true);
    }
  };

  useEffect(() => {
    if (!isSDK) {
      init();
    }
  }, []);

  if (!data.id) return <PageLoading error={isError} />;

  return (
    <DndProvider backend={HTML5Backend}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={`/my/edit/tour/${getTid()}`}>
          <Routes>
            <Route element={<LayoutPage />}>
              <Route path="main" element={<LayoutToolBar />}>
                <Route index element={<Navigate to="basic" replace />} />
                <Route path=":tab" element={<PageMain />} />
              </Route>
              <Route path="sandtable" element={<PageSandTables />} />
              <Route path="*" element={<Navigate to="/main" replace />} />
            </Route>
          </Routes>
          <Noti />
        </BrowserRouter>
      </QueryClientProvider>
    </DndProvider>
  );
}

export default hot(App);
